<template>
  <section class="agent-picker" aria-label="Buying preference">
    <!-- Locked from referral link (incl. while revalidating / hydrating) -->
    <div v-if="showLockedReferral" class="agent-picker__locked">
      <div class="agent-picker__avatar" aria-hidden="true">
        {{ initials(referralAgencyName) }}
      </div>
      <div class="agent-picker__locked-body">
        <p class="agent-picker__eyebrow">Referred by your agent</p>
        <h3 class="agent-picker__name">{{ referralAgencyName || 'Your agent' }}</h3>
        <p class="agent-picker__meta">
          Your quote will be linked to this agent. You don’t need to search or change this.
        </p>
      </div>
      <span class="agent-picker__chip">{{ isReferralLoading ? 'Linking…' : 'Linked' }}</span>
    </div>

    <template v-else-if="!isReferralLoading">
      <header class="agent-picker__header">
        <h3 class="agent-picker__title">How are you buying?</h3>
        <p class="agent-picker__sub">
          Buy directly, or choose an agent so they can support you and earn commission.
        </p>
      </header>

      <div class="agent-picker__options" role="radiogroup" aria-label="Buying channel">
        <button
          type="button"
          class="agent-picker__option"
          :class="{ 'agent-picker__option--active': mode === 'direct' }"
          role="radio"
          :aria-checked="mode === 'direct'"
          @click="setMode('direct')"
        >
          <span class="agent-picker__option-icon" aria-hidden="true">
            <v-icon size="20" :color="mode === 'direct' ? '#184679' : '#94A3B8'">mdi-account</v-icon>
          </span>
          <span class="agent-picker__option-copy">
            <strong>Direct</strong>
            <small>No agent involved</small>
          </span>
          <span class="agent-picker__check" aria-hidden="true">
            <v-icon v-if="mode === 'direct'" size="18" color="#184679">mdi-check-circle</v-icon>
          </span>
        </button>

        <button
          type="button"
          class="agent-picker__option"
          :class="{ 'agent-picker__option--active': mode === 'agent' }"
          role="radio"
          :aria-checked="mode === 'agent'"
          @click="setMode('agent')"
        >
          <span class="agent-picker__option-icon" aria-hidden="true">
            <v-icon size="20" :color="mode === 'agent' ? '#184679' : '#94A3B8'">mdi-account-tie</v-icon>
          </span>
          <span class="agent-picker__option-copy">
            <strong>Through an agent</strong>
            <small>Search and select your agent</small>
          </span>
          <span class="agent-picker__check" aria-hidden="true">
            <v-icon v-if="mode === 'agent'" size="18" color="#184679">mdi-check-circle</v-icon>
          </span>
        </button>
      </div>

      <div v-if="mode === 'agent'" class="agent-picker__search-wrap">
        <v-autocomplete
          v-model="selectedName"
          :items="filteredNames"
          :loading="loading"
          :search-input.sync="search"
          label="Search agent by name"
          placeholder="Start typing a name…"
          outlined
          dense
          hide-no-data
          clearable
          hide-details="auto"
          prepend-inner-icon="mdi-magnify"
          :rules="[requiredAgent]"
          class="agent-picker__autocomplete"
          @change="onAgentPicked"
        />

        <div v-if="selectedAgentCard" class="agent-picker__chosen">
          <div class="agent-picker__avatar agent-picker__avatar--sm" aria-hidden="true">
            {{ initials(selectedAgentCard.agencyName) }}
          </div>
          <div>
            <strong>{{ selectedAgentCard.agencyName }}</strong>
            <small v-if="selectedAgentCard.accountNo || selectedAgentCard.referralCode">
              {{ selectedAgentCard.accountNo || selectedAgentCard.referralCode }}
            </small>
          </div>
        </div>
      </div>
    </template>

    <div v-else class="agent-picker__locked">
      <div class="agent-picker__avatar" aria-hidden="true">…</div>
      <div class="agent-picker__locked-body">
        <p class="agent-picker__eyebrow">Checking referral link</p>
        <h3 class="agent-picker__name">Linking your agent…</h3>
        <p class="agent-picker__meta">
          Hang on — we are confirming the agent from your referral link.
        </p>
      </div>
      <span class="agent-picker__chip">Linking…</span>
    </div>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import { listReferralAgents } from '@/services/referralApi';

export default {
  name: 'CustomerAgentPicker',
  data() {
    return {
      mode: 'direct',
      loading: false,
      agents: [],
      search: null,
      selectedName: null,
      loaded: false,
    };
  },
  computed: {
    ...mapGetters('referral', [
      'hasValidReferral',
      'isReferralLocked',
      'isReferralLoading',
      'referralAgencyName',
      'referralAgent',
      'referralSource',
    ]),
    /** Locked UI while link agent is known or still resolving — never flash Direct mid-load. */
    showLockedReferral() {
      return this.isReferralLocked || (this.isReferralLoading && this.referralSource === 'link' && !!this.referralAgent);
    },
    filteredNames() {
      const q = String(this.search || '').trim().toUpperCase();
      const names = this.agents.map((a) => a.agencyName).filter(Boolean);
      if (!q) return names.slice(0, 50);
      return names.filter((n) => n.toUpperCase().includes(q)).slice(0, 50);
    },
    selectedAgentCard() {
      if (!this.selectedName || !this.hasValidReferral) return null;
      return this.referralAgent;
    },
  },
  watch: {
    showLockedReferral: {
      immediate: true,
      handler(locked) {
        if (locked) {
          this.mode = 'agent';
          this.selectedName = this.referralAgencyName;
        }
      },
    },
    hasValidReferral: {
      immediate: true,
      handler(valid) {
        if (this.showLockedReferral || this.isReferralLoading) return;
        if (valid && this.referralSource === 'manual') {
          this.mode = 'agent';
          this.selectedName = this.referralAgencyName;
        } else if (!valid && !this.isReferralLocked) {
          this.mode = 'direct';
          this.selectedName = null;
        }
      },
    },
  },
  async mounted() {
    await this.$store.dispatch('referral/ensureReferralValidated', this.$route.query);
    if (this.mode === 'agent' || this.showLockedReferral) {
      await this.ensureAgentsLoaded();
    }
  },
  methods: {
    initials(name) {
      const parts = String(name || '')
        .trim()
        .split(/\s+/)
        .filter(Boolean);
      if (!parts.length) return 'A';
      if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
      return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
    },
    requiredAgent(v) {
      if (this.mode !== 'agent' || this.showLockedReferral) return true;
      return !!v || 'Select an agent';
    },
    async setMode(value) {
      if (this.showLockedReferral) return;
      if (this.mode === value) return;
      this.mode = value;
      if (value === 'direct') {
        this.selectedName = null;
        this.search = null;
        await this.$store.dispatch('referral/selectDirect');
        this.$emit('change', null);
        return;
      }
      await this.ensureAgentsLoaded();
    },
    async ensureAgentsLoaded() {
      if (this.loaded || this.loading) return;
      this.loading = true;
      try {
        this.agents = await listReferralAgents();
        this.loaded = true;
      } finally {
        this.loading = false;
      }
    },
    onAgentPicked(name) {
      if (!name) {
        this.$store.dispatch('referral/selectManualAgent', null);
        this.$emit('change', null);
        return;
      }
      const agent = this.agents.find((a) => a.agencyName === name);
      if (!agent) return;
      this.$store.dispatch('referral/selectManualAgent', agent);
      this.$emit('change', agent);
    },
  },
};
</script>

<style scoped>
.agent-picker {
  margin-bottom: 20px;
  padding: 18px 18px 16px;
  border-radius: 12px;
  border: 1px solid #e2e8f0;
  background: linear-gradient(180deg, #f8fafc 0%, #ffffff 48%);
}

.agent-picker__header {
  margin-bottom: 14px;
}

.agent-picker__title {
  margin: 0;
  font-size: 1rem;
  font-weight: 700;
  color: #184679;
  letter-spacing: -0.01em;
}

.agent-picker__sub {
  margin: 4px 0 0;
  font-size: 0.82rem;
  line-height: 1.4;
  color: #64748b;
}

.agent-picker__options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.agent-picker__option {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 12px;
  border-radius: 10px;
  border: 1.5px solid #e2e8f0;
  background: #fff;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
}

.agent-picker__option:hover {
  border-color: #94a3b8;
}

.agent-picker__option--active {
  border-color: #184679;
  background: #f0f7ff;
  box-shadow: 0 0 0 3px rgba(24, 70, 121, 0.08);
}

.agent-picker__option-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: #f1f5f9;
  flex-shrink: 0;
}

.agent-picker__option--active .agent-picker__option-icon {
  background: #dbeafe;
}

.agent-picker__option-copy {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
  flex: 1;
}

.agent-picker__option-copy strong {
  font-size: 0.88rem;
  color: #0f172a;
  font-weight: 700;
}

.agent-picker__option-copy small {
  font-size: 0.72rem;
  color: #64748b;
  line-height: 1.25;
}

.agent-picker__check {
  width: 18px;
  flex-shrink: 0;
}

.agent-picker__search-wrap {
  margin-top: 14px;
  max-width: 480px;
}

.agent-picker__autocomplete {
  background: #fff;
}

.agent-picker__chosen {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  padding: 10px 12px;
  border-radius: 10px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
}

.agent-picker__chosen strong {
  display: block;
  font-size: 0.86rem;
  color: #184679;
}

.agent-picker__chosen small {
  display: block;
  margin-top: 1px;
  font-size: 0.72rem;
  color: #64748b;
  letter-spacing: 0.02em;
}

.agent-picker__locked {
  display: flex;
  align-items: flex-start;
  gap: 12px;
}

.agent-picker__locked-body {
  flex: 1;
  min-width: 0;
}

.agent-picker__eyebrow {
  margin: 0;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: #64748b;
}

.agent-picker__name {
  margin: 2px 0 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #184679;
}

.agent-picker__meta {
  margin: 4px 0 0;
  font-size: 0.8rem;
  line-height: 1.4;
  color: #64748b;
}

.agent-picker__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: #184679;
  color: #fff;
  font-size: 0.78rem;
  font-weight: 700;
  flex-shrink: 0;
}

.agent-picker__avatar--sm {
  width: 34px;
  height: 34px;
  font-size: 0.7rem;
  background: #1e4276;
}

.agent-picker__chip {
  flex-shrink: 0;
  margin-top: 2px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #166534;
  background: #dcfce7;
  border: 1px solid #86efac;
}

@media (max-width: 640px) {
  .agent-picker__options {
    grid-template-columns: 1fr;
  }

  .agent-picker__locked {
    flex-wrap: wrap;
  }
}
</style>
